<template>
  <div>
  <CrudBase
    title="Usuários"
    singular-title="Usuário"
    api-endpoint="/api/v1/users"
    :columns="columns"
    :form-fields="formFields"
    :custom-update-endpoint="getUpdateEndpoint"
    :can-delete-item="(item) => !item.master"
    text-action-buttons
  >
    <template #form="{ form }">
      <div class="space-y-4">
        <!-- Foto de perfil (mesmo fluxo de crop que Perfil / Agentes) -->
        <div class="flex justify-center mb-6">
          <ImageCropUpload
            v-model="form.profile_image"
            upload-strategy="deferred"
            size="md"
            :name="`${form.first_name || ''} ${form.last_name || ''}`.trim() || 'Usuário'"
            label-under-photo-color-class="text-slate-200"
            :max-file-size="USER_PROFILE_IMAGE_MAX_BYTES"
            upload-filename="profile.jpg"
            lightbox-alt-suffix="Foto do usuário"
            confirm-delete-message="Remover a foto deste usuário? (salve o formulário para aplicar.)"
          />
        </div>

        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label for="first_name" class="block text-sm font-medium text-slate-300 mb-1">
              Nome <span class="text-red-400">*</span>
            </label>
            <input
              v-model="form.first_name"
              id="first_name"
              type="text"
              class="w-full px-3 py-2 rounded-lg border border-slate-500/80 bg-slate-800/55 text-slate-100 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-cyan-400/40 focus:border-cyan-400/70"
              required
            />
          </div>

          <div>
            <label for="last_name" class="block text-sm font-medium text-slate-300 mb-1">
              Sobrenome <span class="text-red-400">*</span>
            </label>
            <input
              v-model="form.last_name"
              id="last_name"
              type="text"
              class="w-full px-3 py-2 rounded-lg border border-slate-500/80 bg-slate-800/55 text-slate-100 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-cyan-400/40 focus:border-cyan-400/70"
              required
            />
          </div>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label for="email" class="block text-sm font-medium text-slate-300 mb-1">
              E-mail <span class="text-red-400">*</span>
            </label>
            <input
              v-model="form.email"
              id="email"
              type="email"
              class="w-full px-3 py-2 rounded-lg border border-slate-500/80 bg-slate-800/55 text-slate-100 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-cyan-400/40 focus:border-cyan-400/70"
              required
            />
          </div>

          <div>
            <label for="phone" class="block text-sm font-medium text-slate-300 mb-1">
              Telefone
            </label>
            <input
              v-model="form.phone"
              id="phone"
              type="text"
              @input="masks.handlePhoneMaskInput"
              placeholder="(00) 00000-0000"
              class="w-full px-3 py-2 rounded-lg border border-slate-500/80 bg-slate-800/55 text-slate-100 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-cyan-400/40 focus:border-cyan-400/70"
            />
          </div>
        </div>

        <div>
          <p class="text-sm text-slate-400 mb-3">
            {{ form.id ? 'Deixe senha e confirmação em branco para manter a senha atual.' : 'Defina uma senha para o novo usuário.' }}
          </p>
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label for="password" class="block text-sm font-medium text-slate-300 mb-1">
                Senha {{ !form.id ? '*' : '' }}
              </label>
              <div class="relative">
                <input
                  v-model="form.password"
                  id="password"
                  :type="showUserPassword ? 'text' : 'password'"
                  autocomplete="new-password"
                  class="w-full pl-3 pr-11 py-2 rounded-lg border border-slate-500/80 bg-slate-800/55 text-slate-100 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-cyan-400/40 focus:border-cyan-400/70"
                  :required="!form.id"
                />
                <button
                  type="button"
                  class="absolute inset-y-0 right-0 flex items-center justify-center w-11 text-slate-400 hover:text-[#2AD7DE] focus:outline-none focus-visible:text-[#2AD7DE] rounded-r-lg"
                  :aria-label="showUserPassword ? 'Ocultar senha' : 'Mostrar senha'"
                  :aria-pressed="showUserPassword"
                  @click="showUserPassword = !showUserPassword"
                >
                  <svg v-if="!showUserPassword" class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                  <svg v-else class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                  </svg>
                </button>
              </div>
            </div>

            <div>
              <label for="password_confirmation" class="block text-sm font-medium text-slate-300 mb-1">
                Confirmar Senha {{ !form.id ? '*' : '' }}
              </label>
              <div class="relative">
                <input
                  v-model="form.password_confirmation"
                  id="password_confirmation"
                  :type="showUserPasswordConfirmation ? 'text' : 'password'"
                  autocomplete="new-password"
                  class="w-full pl-3 pr-11 py-2 rounded-lg border border-slate-500/80 bg-slate-800/55 text-slate-100 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-cyan-400/40 focus:border-cyan-400/70"
                  :required="!form.id"
                />
                <button
                  type="button"
                  class="absolute inset-y-0 right-0 flex items-center justify-center w-11 text-slate-400 hover:text-[#2AD7DE] focus:outline-none focus-visible:text-[#2AD7DE] rounded-r-lg"
                  :aria-label="showUserPasswordConfirmation ? 'Ocultar confirmação de senha' : 'Mostrar confirmação de senha'"
                  :aria-pressed="showUserPasswordConfirmation"
                  @click="showUserPasswordConfirmation = !showUserPasswordConfirmation"
                >
                  <svg v-if="!showUserPasswordConfirmation" class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                  <svg v-else class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>

        <div class="border-t border-slate-700/70 pt-4 space-y-2">
          <label class="flex items-center gap-3 cursor-pointer select-none">
            <input
              v-model="form.admin"
              type="checkbox"
              class="rounded border-slate-500 text-cyan-500 focus:ring-cyan-400/50"
              :disabled="!!form.master"
            />
            <span class="text-sm text-slate-200">Administrador do ambiente</span>
          </label>
          <p v-if="form.master" class="text-xs text-slate-500 leading-snug">
            Usuário master: sempre administrador; não é possível alterar nem excluir este registro.
          </p>
        </div>

        <div class="border-t border-slate-700/70 pt-4">
          <h3 class="text-lg font-medium text-slate-100 mb-3">Grupo de Coordenadores Disponível no Chat</h3>
          <div
            v-if="
              coordinatorGroupOptions.length > 0 &&
              (!Array.isArray(form.coordinator_group_ids) || form.coordinator_group_ids.length === 0)
            "
            class="mb-3 text-sm text-amber-50/95 leading-relaxed font-medium bg-amber-500/15 border border-amber-400/45 rounded-lg px-3 py-3 shadow-[0_0_0_1px_rgba(251,191,36,0.08)]"
          >
            Selecione pelo menos um grupo para habilitar o uso do chat para este usuário. Apenas os coordenadores desses grupos serão
            exibidos no chat. Sem grupos selecionados, nenhum coordenador ficará disponível.
          </div>
          <div v-if="coordinatorGroupOptions.length === 0" class="text-sm text-slate-500">
            Nenhum grupo cadastrado. Crie em Agentes → Grupos de Coordenadores.
          </div>
          <div
            v-else
            class="max-h-48 overflow-y-auto rounded-lg border border-slate-600/80 bg-slate-900/40 divide-y divide-slate-700/60"
          >
            <label
              v-for="g in coordinatorGroupOptions"
              :key="g.id"
              class="flex items-center gap-3 px-3 py-2.5 hover:bg-slate-800/50 cursor-pointer"
            >
              <input
                type="checkbox"
                class="rounded border-slate-500 text-cyan-500 focus:ring-cyan-400/50"
                :checked="isCoordinatorGroupSelected(form, g.id)"
                @change="toggleCoordinatorGroup(form, g.id, $event.target.checked)"
              />
              <span class="text-sm text-slate-100">{{ g.name }}</span>
            </label>
          </div>
        </div>

        <div v-if="form.id" class="border-t border-slate-700/70 pt-4">
          <h3 class="text-lg font-medium text-slate-100 mb-2">Acesso externo (widget / integrações)</h3>
          <p class="text-sm text-slate-400 mb-4 leading-snug">
            Token para autenticar este usuário fora do PowerAgent (ex.: chat embutido). Use
            <code class="text-cyan-300/90 text-xs">Authorization: Bearer &lt;token&gt;</code>
            ou
            <code class="text-cyan-300/90 text-xs">POST /api/v1/auth/external_exchange</code>.
            O valor completo só aparece uma vez ao gerar.
          </p>
          <div v-if="externalTokenErrorFor(form)" class="mb-3 text-sm text-red-300">
            {{ externalTokenErrorFor(form) }}
          </div>
          <div
            v-if="plainTokenFor(form)"
            class="mb-4 p-3 rounded-lg bg-amber-500/10 border border-amber-400/35 text-amber-100 text-sm space-y-2"
          >
            <p class="font-medium">Copie agora — não será possível ver de novo:</p>
            <code class="block break-all text-xs text-amber-50/95 select-all">{{ plainTokenFor(form) }}</code>
            <button
              type="button"
              class="text-xs px-2 py-1 rounded border border-amber-400/50 hover:bg-amber-500/15"
              @click="copyPlainExternalToken(form)"
            >
              Copiar
            </button>
          </div>
          <div class="flex flex-wrap gap-2">
            <button
              type="button"
              :disabled="externalTokenLoading"
              class="px-4 py-2 rounded-lg bg-[#2AD7DE] hover:bg-[#5de9f0] text-slate-950 text-sm font-semibold transition disabled:opacity-50"
              @click="openGrantTokenConfirm(form)"
            >
              {{ externalTokenLoading ? '…' : form.external_access_token_configured ? 'Gerar novo token' : 'Gerar token' }}
            </button>
            <button
              v-if="form.external_access_token_configured"
              type="button"
              :disabled="externalTokenLoading"
              class="px-4 py-2 rounded-lg border border-red-400/45 text-red-200 text-sm hover:bg-red-500/10 transition disabled:opacity-50"
              @click="openRevokeTokenConfirm(form)"
            >
              Revogar token
            </button>
          </div>
          <p class="text-xs text-slate-500 mt-3">
            Estado:
            <span :class="form.external_access_token_configured ? 'text-emerald-400' : 'text-slate-400'">
              {{ form.external_access_token_configured ? 'token ativo' : 'nenhum token' }}
            </span>
          </p>
        </div>
        <p v-else class="text-sm text-slate-500 border-t border-slate-700/70 pt-4">
          Salve o usuário para gerenciar o token de acesso externo.
        </p>
      </div>
    </template>
  </CrudBase>

  <!-- Mesmo padrão visual que salvar / excluir no CrudBase (z acima do modal de edição) -->
  <div
    v-if="tokenConfirm"
    class="fixed inset-0 bg-slate-950/60 backdrop-blur-sm flex items-center justify-center z-[60] p-4"
    @click.self="closeTokenConfirm"
  >
    <div class="bg-[#435b7c]/95 border border-cyan-400/25 rounded-xl shadow-2xl shadow-black/45 max-w-md w-full p-6">
      <h3 class="flex items-center text-lg font-semibold text-slate-100 mb-4">
        <svg class="w-6 h-6 text-amber-500 mr-2 shrink-0" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
          <path
            fill-rule="evenodd"
            d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l6.518 11.59C19.02 16.92 18.145 18 16.757 18H3.243c-1.388 0-2.263-1.08-1.504-2.31l6.518-11.59zM11 14a1 1 0 10-2 0 1 1 0 002 0zm-1-8a1 1 0 00-.894.553L9 7v4a1 1 0 002 0V7l-.106-.447A1 1 0 0010 6z"
            clip-rule="evenodd"
          />
        </svg>
        Confirmação!
      </h3>
      <p class="text-slate-300 mb-6 whitespace-pre-line">{{ tokenConfirmMessage }}</p>
      <div class="flex justify-end space-x-3">
        <button
          type="button"
          :disabled="externalTokenLoading"
          :class="[
            'px-4 py-2 rounded-lg font-semibold transition disabled:opacity-50',
            tokenConfirm.action === 'revoke'
              ? 'bg-red-600 text-white hover:bg-red-500'
              : 'bg-[#2AD7DE] hover:bg-[#5de9f0] text-slate-950'
          ]"
          @click="confirmTokenAction"
        >
          {{ externalTokenLoading ? '…' : 'Sim' }}
        </button>
        <button
          type="button"
          :disabled="externalTokenLoading"
          class="px-4 py-2 border border-slate-600 rounded-lg text-slate-300 hover:bg-slate-800/70 transition disabled:opacity-50"
          @click="closeTokenConfirm"
        >
          Não
        </button>
      </div>
    </div>
  </div>
  </div>
</template>

<script>
import CrudBase from './CrudBase.vue'
import ImageCropUpload from './ImageCropUpload.vue'
import { useBrazilianMasks } from '../composables/useBrazilianMasks.js'
import { useApi } from '../composables/useApi.js'

/** Alinhado a `User` — `validates_blob_byte_size profile_image: 1.megabytes` */
const USER_PROFILE_IMAGE_MAX_BYTES = 1024 * 1024

export default {
  components: {
    CrudBase,
    ImageCropUpload
  },

  data() {
    return {
      USER_PROFILE_IMAGE_MAX_BYTES,
      showUserPassword: false,
      showUserPasswordConfirmation: false,
      externalTokenLoading: false,
      externalTokenErrorByUserId: {},
      externalTokenPlainByUserId: {},
      /** { action: 'grant' | 'revoke', form } — form é o objeto do slot do CrudBase */
      tokenConfirm: null,
      masks: useBrazilianMasks(),
      columns: [
        {
          key: 'name',
          label: 'Nome',
          format: (value, item) => `${item.first_name || ''} ${item.last_name || ''}`.trim() || '-'
        },
        { key: 'email', label: 'E-mail' },
        { key: 'phone', label: 'Telefone' },
        {
          key: 'coordinator_groups_label',
          label: 'Grupos (chat)',
          format: (value) => value || '—'
        },
        {
          key: 'access',
          label: 'Permissão',
          format: (_value, item) => {
            if (item.master) return 'Master'
            if (item.admin) return 'Administrador'
            return 'Usuário'
          }
        }
      ],
      coordinatorGroupOptions: [],
      formFields: {
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        profile_image: '',
        password: '',
        password_confirmation: '',
        admin: false,
        external_access_token_configured: false,
        coordinator_group_ids: []
      }
    }
  },

  mounted() {
    this.loadCoordinatorGroupOptions()
  },

  computed: {
    tokenConfirmMessage() {
      if (!this.tokenConfirm) return ''
      if (this.tokenConfirm.action === 'revoke') {
        return 'Revogar o token? Integrações externas deixarão de autenticar até gerar outro.'
      }
      const has = !!this.tokenConfirm.form?.external_access_token_configured
      return has
        ? 'Gerar um novo token invalida o anterior. Continuar?'
        : 'Gerar um token de acesso externo? Guarde-o em local seguro.'
    }
  },

  methods: {
    async loadCoordinatorGroupOptions() {
      const { get } = useApi()
      const { data, error } = await get('/api/v1/coordinator_groups')
      if (error || !Array.isArray(data)) {
        this.coordinatorGroupOptions = []
        return
      }
      this.coordinatorGroupOptions = data.map((g) => ({ id: g.id, name: g.name }))
    },

    isCoordinatorGroupSelected(form, id) {
      const ids = Array.isArray(form.coordinator_group_ids) ? form.coordinator_group_ids : []
      return ids.includes(id)
    },

    toggleCoordinatorGroup(form, id, checked) {
      const cur = Array.isArray(form.coordinator_group_ids) ? [...form.coordinator_group_ids] : []
      const set = new Set(cur)
      if (checked) {
        set.add(id)
      } else {
        set.delete(id)
      }
      form.coordinator_group_ids = Array.from(set).sort((a, b) => a - b)
    },

    getUpdateEndpoint(id) {
      return `/api/v1/users/${id}/update_user`
    },

    plainTokenFor(form) {
      if (!form?.id) return ''
      return this.externalTokenPlainByUserId[form.id] || ''
    },

    externalTokenErrorFor(form) {
      if (!form?.id) return ''
      return this.externalTokenErrorByUserId[form.id] || ''
    },

    openGrantTokenConfirm(form) {
      if (!form?.id) return
      this.tokenConfirm = { action: 'grant', form }
    },

    openRevokeTokenConfirm(form) {
      if (!form?.id) return
      this.tokenConfirm = { action: 'revoke', form }
    },

    closeTokenConfirm() {
      if (this.externalTokenLoading) return
      this.tokenConfirm = null
    },

    async confirmTokenAction() {
      if (!this.tokenConfirm?.form?.id) return
      const { action, form } = this.tokenConfirm
      if (action === 'grant') {
        await this.executeGrantExternalAccessToken(form)
      } else if (action === 'revoke') {
        await this.executeRevokeExternalAccessToken(form)
      }
      /* Em erro, fechar o modal: o aviso fica na área do formulário */
      if (!this.externalTokenLoading && this.tokenConfirm) {
        this.tokenConfirm = null
      }
    },

    async executeGrantExternalAccessToken(form) {
      if (!form?.id) return
      this.externalTokenErrorByUserId[form.id] = ''
      delete this.externalTokenPlainByUserId[form.id]
      this.externalTokenLoading = true
      try {
        const { post } = useApi()
        const { data, error } = await post(`/api/v1/users/${form.id}/external_access_token`, {})
        if (error) {
          const msg = error.response?.data?.errors
          const text = Array.isArray(msg)
            ? msg.join(', ')
            : error.response?.data?.error || 'Falha ao gerar token.'
          this.externalTokenErrorByUserId[form.id] = text
          return
        }
        this.externalTokenPlainByUserId[form.id] = data.external_access_token || ''
        form.external_access_token_configured = true
        this.tokenConfirm = null
      } finally {
        this.externalTokenLoading = false
      }
    },

    async executeRevokeExternalAccessToken(form) {
      if (!form?.id) return
      this.externalTokenErrorByUserId[form.id] = ''
      this.externalTokenLoading = true
      try {
        const { remove } = useApi()
        const { data, error } = await remove(`/api/v1/users/${form.id}/external_access_token`)
        if (error) {
          this.externalTokenErrorByUserId[form.id] =
            error.response?.data?.error || 'Falha ao revogar.'
          return
        }
        delete this.externalTokenPlainByUserId[form.id]
        if (data && typeof data.external_access_token_configured === 'boolean') {
          form.external_access_token_configured = data.external_access_token_configured
        } else {
          form.external_access_token_configured = false
        }
        this.tokenConfirm = null
      } finally {
        this.externalTokenLoading = false
      }
    },

    async copyPlainExternalToken(form) {
      const token = this.plainTokenFor(form)
      if (!token) return
      try {
        await navigator.clipboard.writeText(token)
      } catch {
        /* ignore */
      }
    }
  }
}
</script>
